import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useUploadStaffingRequest } from "@/hooks/use-staffing-requests";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadRequestDialog({ open, onOpenChange }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const upload = useUploadStaffingRequest();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    // Imports are always Resource Plans (the only supported source).
    upload.mutate(
      { file, sourceType: "ResourcePlan" },
      {
        onSuccess: () => {
          setFile(null);
          onOpenChange(false);
        },
      },
    );
  }

  function handleOpenChange(next: boolean) {
    if (!next) setFile(null);
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Resource Plan</DialogTitle>
          <DialogDescription>
            Upload a resource plan (.csv or .xlsx) or a completed{" "}
            <a href="/api/staffing-requests/template" download className="underline">
              Staffing Request template
            </a>
            . The system will parse one line per role and create a request for mapping.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="file-upload">Resource Plan File</Label>
            <input
              id="file-upload"
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => {
                const picked = e.target.files?.[0];
                setFile(picked ?? null);
              }}
              className="block w-full rounded-md border border-border bg-card px-3 py-2 text-[12px] text-foreground file:mr-3 file:rounded file:border-0 file:bg-muted file:px-2 file:py-0.5 file:text-[11px] file:font-medium"
            />
            <p className="text-[10px] text-muted-foreground">Accepted: .csv, .xlsx</p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={upload.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!file || upload.isPending}>
              {upload.isPending ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
